import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle,
  Grid, IconButton, InputAdornment, TextField, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Close as CloseIcon, DragIndicator as DragIndicatorIcon,
  Search as SearchIcon, Article as TemplateIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import SortableModelList from '../evolution/SortableModelList';
import DocumentTextTemplateFormDialog, { type DocumentTextTemplateCategory } from './DocumentTextTemplateFormDialog';
import {
  filterTemplatesByTitle,
  orderPrescriptionTemplates,
} from './PrescriptionTemplateChipPicker';
import {
  CLOUD_API_THEME,
  cloudApiCampaignCardSx,
  cloudApiDialogContentSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiInputFieldSx,
  cloudApiOutlinedButtonSx,
  cloudApiPrimaryButtonSx,
} from '../../pages/whatsappCloudApiTheme';

const CATEGORY_META: Record<DocumentTextTemplateCategory, { title: string; singular: string }> = {
  report: { title: 'Relatórios Médicos Pré-preenchidos', singular: 'relatório médico' },
  evolution: { title: 'Evoluções Pré-preenchidas', singular: 'evolução' },
  surgical_risk: { title: 'Riscos Cirúrgicos Pré-preenchidos', singular: 'risco cirúrgico' },
};

const scopeLabel = (scope?: string) => (scope === 'professional' ? 'Somente eu' : 'Toda a clínica');

export interface DocumentTextTemplateManagerDialogProps {
  open: boolean;
  onClose: () => void;
  category: DocumentTextTemplateCategory;
  professionalId?: string | null;
  onTemplatesChanged?: () => void;
  onApply?: (template: any) => void;
}

const DocumentTextTemplateManagerDialog: React.FC<DocumentTextTemplateManagerDialogProps> = ({
  open,
  onClose,
  category,
  professionalId,
  onTemplatesChanged,
  onApply,
}) => {
  const meta = CATEGORY_META[category];
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [alphaSort, setAlphaSort] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { category };
      if (professionalId) params.professional_id = professionalId;
      const { data } = await axios.get(getApiUrl('/clinic/document-text-templates'), { params });
      setTemplates(data?.data || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [category, professionalId]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setAlphaSort(false);
      setReorderMode(false);
      void loadTemplates();
    }
  }, [open, loadTemplates]);

  const orderedTemplates = useMemo(
    () => orderPrescriptionTemplates(templates, alphaSort && !reorderMode ? 'alpha' : 'default'),
    [templates, alphaSort, reorderMode],
  );

  const filteredTemplates = useMemo(
    () => filterTemplatesByTitle(orderedTemplates, search),
    [orderedTemplates, search],
  );

  const handleReorder = async (orderedIds: string[]) => {
    await axios.put(getApiUrl('/clinic/document-text-templates/reorder'), { ids: orderedIds });
    await loadTemplates();
    onTemplatesChanged?.();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir este modelo?')) return;
    await axios.delete(getApiUrl(`/clinic/document-text-templates/${id}`));
    await loadTemplates();
    onTemplatesChanged?.();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: cloudApiDialogPaperSx }}>
        <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon sx={cloudApiDialogHeaderIconSx} />
            {meta.title}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 18 }} />} onClick={() => { setEditing(null); setFormOpen(true); }} sx={cloudApiPrimaryButtonSx}>
              Novo pré-preenchido
            </Button>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={cloudApiDialogContentSx}>
          {loading ? (
            <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slateFaint, py: 2 }}>Carregando modelos...</Typography>
          ) : templates.length === 0 ? (
            <Alert severity="info">
              Nenhum modelo cadastrado. Crie um pré-preenchido para agilizar o registro de {meta.singular}.
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Pesquisar por título..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flex: 1, minWidth: 180, ...cloudApiInputFieldSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: CLOUD_API_THEME.slateFaint, fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant={alphaSort ? 'contained' : 'outlined'}
                  onClick={() => { setAlphaSort((v) => !v); setReorderMode(false); }}
                  sx={alphaSort ? cloudApiPrimaryButtonSx : cloudApiOutlinedButtonSx}
                >
                  Ordem A–Z
                </Button>
                <Button
                  size="small"
                  variant={reorderMode ? 'contained' : 'outlined'}
                  startIcon={<DragIndicatorIcon sx={{ fontSize: 18 }} />}
                  onClick={() => { setReorderMode((v) => !v); setAlphaSort(false); }}
                  sx={reorderMode ? cloudApiPrimaryButtonSx : cloudApiOutlinedButtonSx}
                >
                  {reorderMode ? 'Concluir' : 'Reordenar'}
                </Button>
              </Box>

              {reorderMode && !search.trim() ? (
                <SortableModelList
                  items={orderedTemplates.map((t) => ({ id: String(t.id), name: t.name || 'Modelo' }))}
                  onReorder={handleReorder}
                />
              ) : filteredTemplates.length === 0 ? (
                <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slateFaint, py: 2 }}>
                  Nenhum modelo encontrado para &quot;{search.trim()}&quot;.
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {filteredTemplates.map((tpl: any, idx: number) => (
                    <Grid item xs={12} md={6} key={tpl.id}>
                      <Card sx={{ ...cloudApiCampaignCardSx, p: 0 }}>
                        <CardContent sx={{ p: 1.75 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            {idx + 1}. {tpl.name}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={scopeLabel(tpl.scope)} sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f1f5f9' }} />
                            {tpl.specialty && (
                              <Chip size="small" label={tpl.specialty} sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#ecfdf5', color: '#047857' }} />
                            )}
                            <Chip size="small" label="Texto livre" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f1f5f9' }} />
                          </Box>
                          <Box sx={{ mt: 1.25, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {onApply && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => { onApply(tpl); onClose(); }}
                                sx={{ ...cloudApiPrimaryButtonSx, minHeight: 28, fontSize: '0.75rem' }}
                              >
                                Usar
                              </Button>
                            )}
                            <Button size="small" variant="outlined" onClick={() => { setEditing(tpl); setFormOpen(true); }} sx={{ textTransform: 'none', minHeight: 28, fontSize: '0.75rem' }}>
                              Editar
                            </Button>
                            <Button size="small" color="error" onClick={() => void handleDelete(tpl.id)} sx={{ textTransform: 'none', minHeight: 28, fontSize: '0.75rem' }}>
                              Excluir
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <DocumentTextTemplateFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        category={category}
        professionalId={professionalId}
        editing={editing}
        onSaved={() => {
          void loadTemplates();
          onTemplatesChanged?.();
        }}
      />
    </>
  );
};

export default DocumentTextTemplateManagerDialog;
